import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/configureStore';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { API } from 'aws-amplify';
import { newNotification, Notification } from '../../actions/uiActions';
import { logToDatadog } from '../../services/datadog';
import { AddressFind, Cell, DropLocation, CreateDropLocation } from '@m4a3/api-client';
import { Switch, Label, Field } from '@headlessui/react';
import { updateAddressEnabledStatus } from '../../actions/routePlannerActions';
import { getGeofences } from '../../actions/geofenceActions';
import { getObstacles } from '../../actions/obstacleActions';
import { selectObstacles } from '../../reducers/obstaclesReducer';
import { blanchCellId } from '../../constants';
import { Obstacle } from '../../models/obstacle.interface';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point, polygon } from '@turf/helpers';
import { AddressFindRemark } from '@m4a3/api-client/dist/models/address-find-remark.interface';
import { getCells } from '../../actions/orderActions';
import SelectDropdown from '../../components/SelectDropdown/SelectDropdown';
import { addressRemarkDropdownOptions } from '../../utils/addressFindUtils';
import { orderfulApi } from '../../services/api-instances';
import { getLatestJwtToken } from '../../services/amplify-auth';
import { Wrapper } from '@googlemaps/react-wrapper';
import GoogleMap from './components/GoogleMap3D';
import LidarVisualization3D from './components/LidarVisualization3D';

// Simplified LiDAR point interface
interface LidarPoint {
  height: number; // Original AMSL height
  intensity: number;
  lat: number;
  lng: number;
  metresFromPoint: number;
  heightAGL: number; // Height Above Ground Level (AGL)
  heightAMSL: number; // Original AMSL height for reference
  distanceFromPoint: number;
  x?: number;
  y?: number;
}

interface DropPointSuggestorV2Props {
  notify: (notification: Notification) => void;
  updateAddressEnabledStatus: (addressId: string, isEnabled: boolean) => Promise<void>;
  getCells: () => void;
  cells: Cell[];
  selectedCellId: number;
  geofences: any;
  getGeofences: () => void;
  getObstacles: (cellId: number) => void;
}

function DropPointSuggestorV2({
  notify,
  updateAddressEnabledStatus,
  getCells,
  cells,
  selectedCellId,
  geofences,
  getGeofences,
  getObstacles
}: Readonly<DropPointSuggestorV2Props>) {
  const obstacles: Obstacle[] = useSelector(selectObstacles);
  const mapRef = useRef<google.maps.Map>();
  const [eircode, setEircode] = useState<string>('');
  const [isAddressEnabled, setIsAddressEnabled] = useState<boolean>(false);
  const [addressInfo, setAddressInfo] = useState<AddressFind>(null);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoadingAddressInfo, setIsLoadingAddressInfo] = useState<boolean>(false);
  const [isEnablingAddress, setIsEnablingAddress] = useState<boolean>(false);
  const [shouldShowModal, setShouldShowModal] = useState<boolean>(false);
  const [lidarData, setLidarData] = useState<LidarPoint[]>([]);
  const [heightData, setHeightData] = useState<number | null>(null);
  const [isLoadingLidarData, setIsLoadingLidarData] = useState<boolean>(false);
  const [columnWidth, setColumnWidth] = useState<number>(3); // Default to Blanchardstown radius
  const [addressRemarkText, setAddressRemarkText] = useState<string>('');
  const [manualCoords, setManualCoords] = useState<string>('');
  const [manualCellId, setManualCellId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'eircode' | 'coordinates'>('eircode');

  // Function to get delivery area radius based on cell ID
  const getDeliveryRadiusForCell = (cellId?: number): number => {
    const activeCellId = cellId || blanchCellId;
    // Cell 7 (Blanchardstown) gets 2.25m, all other cells get 3m
    return activeCellId === blanchCellId ? 2.25 : 3;
  };

  // Function to determine risk level based on height data
  const getRiskLevel = (height?: number | null): 'HIGH' | 'LOW' | 'UNKNOWN' => {
    if (height === null || height === undefined || isNaN(height)) {
      return 'UNKNOWN';
    }
    if (height >= 1.0) {
      return 'HIGH';    // Unsafe - obstacles detected
    } else {
      return 'LOW';     // Safe - ground level, suitable for drone landing
    }
  };

  // Function to filter drop locations - show all that have been used/approved, filter out pure suggestions
  const shouldShowDropLocation = (dropLocation: DropLocation): boolean => {
    // Show all drop locations that have been used or approved, regardless of whether they were originally suggested
    if (dropLocation.okDeliveries && dropLocation.okDeliveries > 0) return true; // Has successful deliveries
    if (dropLocation.isBackGardenApproved) return true; // Has been approved
    if (dropLocation.userCreated) return true; // Was created by user
    if (dropLocation.risk) return true; // Has been risk assessed
    if (!dropLocation.suggestedForAddressFindId) return true; // Not a suggestion, so it's a real drop location
    
    // Only filter out pure suggestions that haven't been verified or used
    return true;
  };

  // Function to save a suggested drop location
  const saveSuggestedDropLocation = async () => {
    console.log('🚀 Save drop location clicked', {
      markerPosition,
      addressInfo: !!addressInfo,
      heightData,
      activeTab
    });

    if (!markerPosition) {
      notify({ variant: 'error', message: 'Please select a location on the map' });
      return;
    }

    if (activeTab === 'eircode' && !addressInfo) {
      notify({ variant: 'error', message: 'Please load address information first' });
      return;
    }

    // Get current cell ID to check if testing mode should be enabled
    const currentCellId = activeTab === 'eircode' 
      ? (addressInfo?.cellId || blanchCellId)
      : (parseInt(manualCellId) || blanchCellId);
    
    // Bypass safety restrictions for cell 5 (testing)
    const isTestingCell = currentCellId === 5;

    if (!isTestingCell && (heightData === null || heightData === undefined)) {
      notify({ variant: 'error', message: 'Height data is still loading, please wait' });
      return;
    }

    const riskLevel = getRiskLevel(heightData);
    
    // Block high risk locations (obstacles detected) unless it's testing cell 5
    if (riskLevel === 'HIGH' && !isTestingCell) {
      notify({
        variant: 'error',
        message: 'Cannot save unsafe drop location (obstacles detected ≥ 1.0m height)'
      });
      return;
    }

          try {
        setIsSavingDropLocation(true);

      const body: CreateDropLocation = {
        latitude: markerPosition.lat,
        longitude: markerPosition.lng,
        locationId: activeTab === 'eircode' ? addressInfo.eircode : `coords_${markerPosition.lat.toFixed(6)}_${markerPosition.lng.toFixed(6)}`,
        address: activeTab === 'eircode' ? addressInfo.address : `Coordinates: ${markerPosition.lat.toFixed(6)}, ${markerPosition.lng.toFixed(6)}`,
        zoneId: 1,
        isBackGardenApproved: false,
        userCreated: true, // Mark as user created for coordinate mode
        suggestedForAddressFindId: activeTab === 'eircode' ? addressInfo.id : null,
        token: null,
        remarks: []
      };

      // Use admin-api instead of deliveryApi
      const token = await getLatestJwtToken();
      const adminUrl = process.env.REACT_APP_ADMIN_URL;
      const url = `${adminUrl}/drop-location`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      notify({
        variant: 'success',
        message: `Drop location saved successfully (${riskLevel.toLowerCase()} risk, ${heightData?.toFixed(1) || 'unknown'}m height)${isTestingCell ? ' - TEST MODE ENABLED' : ''}`
      });

      // Refresh address info to show the new suggested drop location (only for eircode mode)
      if (activeTab === 'eircode') {
        await loadAddressInfo();
      }

    } catch (error) {
      logToDatadog('error_saving_suggested_drop_location', { 
        error, 
        markerPosition, 
        heightData, 
        riskLevel,
        addressInfo: addressInfo?.id 
      }, 'error');
      notify({
        variant: 'error',
        message: error?.errorMessage || 'Failed to save drop location'
      });
    } finally {
      setIsSavingDropLocation(false);
    }
  };
  const [addressRemarks, setAddressRemarks] = useState<AddressFindRemark[]>([]);
  const [mapLoadError, setMapLoadError] = useState<string | null>(null);
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedDropLocationId, setSelectedDropLocationId] = useState<number | null>(null);
  const [isSavingDropLocation, setIsSavingDropLocation] = useState<boolean>(false);

  const [osmRiskData, setOsmRiskData] = useState<any>(null);
  const [isLoadingOsmData, setIsLoadingOsmData] = useState<boolean>(false);
  const [riskEvaluationData, setRiskEvaluationData] = useState<string | null>(null);
  const [isLoadingRiskEvaluation, setIsLoadingRiskEvaluation] = useState<boolean>(false);
  const [riskEvaluationError, setRiskEvaluationError] = useState<string | null>(null);
  const [showBuildingRiskTooltip, setShowBuildingRiskTooltip] = useState<boolean>(false);
  const [coordinateDropLocations, setCoordinateDropLocations] = useState<DropLocation[]>([]);
  const [isLoadingCoordinateDropLocations, setIsLoadingCoordinateDropLocations] = useState<boolean>(false);
  const [lastCoordinateApiCall, setLastCoordinateApiCall] = useState<{ lat: number; lng: number } | null>(null);

  // Function to fetch LiDAR data from the geo API
  const fetchLidarData = async (latitude: number, longitude: number, radius: number = 10, cellId?: number): Promise<LidarPoint[]> => {
    try {
      const activeCellId = cellId || blanchCellId;
      const response = await API.get('api', `/geo/lidar?latitude=${latitude}&longitude=${longitude}&radius=${radius}&cellId=${activeCellId}`, {});
      
      // First pass: get all AMSL heights to find the ground level (minimum height)
      const allHeights = response.map((point: any) => point.height);
      const groundHeight = Math.min(...allHeights); // Lowest point is ground level
      
      // Transform API response to match expected interface
      const transformedData: LidarPoint[] = response.map((point: any) => {
        const deltaLat = point.lat - latitude;
        const deltaLng = point.lng - longitude;
        
        // Use polar coordinates for accurate positioning
        // The API provides accurate distance, so use that instead of lat/lng conversion
        const distance = point.metresFromPoint || 0;
        
        // Calculate angle from lat/lng differences (bearing from center point)
        const angle = Math.atan2(deltaLng, deltaLat); // atan2(x, y) for proper quadrant
        
        // Convert to cartesian coordinates using accurate distance
        const x = distance * Math.sin(angle); // East-West (longitude direction)
        const y = distance * Math.cos(angle); // North-South (latitude direction)
        
        // Convert AMSL to AGL using the lowest LiDAR point as ground reference
        const heightAGL = Math.max(0, point.height - groundHeight);
        
        return {
          height: point.height, // Original AMSL height
          intensity: point.intensity,
          lat: point.lat,
          lng: point.lng,
          metresFromPoint: point.metresFromPoint,
          heightAGL: heightAGL, // Height Above Ground Level (AGL)
          heightAMSL: point.height, // Original AMSL height for reference
          distanceFromPoint: point.metresFromPoint,
          x: x,
          y: y
        };
      });
      
      return transformedData;
    } catch (error) {
      logToDatadog('error_fetching_lidar_data', { error, latitude, longitude, radius, cellId }, 'error');
      throw error;
    }
  };

  // Function to fetch height data from the geo API
  const fetchHeightData = async (latitude: number, longitude: number, radius: number): Promise<number> => {
    try {
      const response = await API.get('api', `/geo/height?latitude=${latitude}&longitude=${longitude}&radius=${radius}`, {});
      return response.height;
    } catch (error) {
      logToDatadog('error_fetching_height_data', { error, latitude, longitude, radius }, 'error');
      throw error;
    }
  };

  // Function to fetch OSM risk data
  const fetchOsmRiskData = async (latitude: number, longitude: number): Promise<any> => {
    try {
      const response = await API.get('api', `/drop-location/osm/risk/${latitude}/${longitude}`, {});
      return response;
    } catch (error) {
      logToDatadog('error_fetching_osm_risk_data', { error, latitude, longitude }, 'error');
      throw error;
    }
  };

  // Function to fetch drop locations by coordinates
  const fetchDropLocationsByCoordinates = async (latitude: number, longitude: number): Promise<DropLocation[]> => {
    try {
      const response = await API.get('api', `/addressfind/dropLocations/${latitude}/${longitude}`, {});
      
      // Handle case where API returns single object instead of array
      if (response && typeof response === 'object') {
        if (Array.isArray(response)) {
          return response as DropLocation[];
        } else {
          // Single object - check if it has dropLocationHistory property
          if (response.dropLocationHistory && Array.isArray(response.dropLocationHistory)) {
            return response.dropLocationHistory as DropLocation[];
          } else {
            // Treat single object as a single drop location
            return [response] as DropLocation[];
          }
        }
      }
      
      return [] as DropLocation[];
    } catch (error) {
      logToDatadog('error_fetching_drop_locations_by_coordinates', { error, latitude, longitude }, 'error');
      throw error;
    }
  };

  // Function to fetch risk evaluation data from admin-api
  const fetchRiskEvaluationData = async (latitude: number, longitude: number): Promise<string> => {
    try {
      console.log('🏗️ Calling risk evaluation API with coords:', { latitude, longitude });
      
      // Use direct fetch instead of AWS Amplify API.get() to handle plain text responses
      const token = await getLatestJwtToken();
      const adminUrl = process.env.REACT_APP_ADMIN_URL;
      const url = `${adminUrl}/drop-location/evaluateRisk/${latitude}/${longitude}`;
      
      console.log('🏗️ Calling URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const responseText = await response.text();
      console.log('🏗️ Risk evaluation raw response:', responseText, 'Type:', typeof responseText);
      
      if (responseText && responseText.trim()) {
        const result = responseText.trim();
        console.log('🏗️ Returning result:', result);
        return result;
      } else {
        console.log('🏗️ API returned empty response');
        throw new Error('API returned empty response');
      }
    } catch (error) {
      console.error('🏗️ Risk evaluation API error:', error);
      logToDatadog('error_fetching_risk_evaluation_data', { error, latitude, longitude }, 'error');
      throw error;
    }
  };

  useEffect(() => {
    if (addressInfo?.address_latitude && addressInfo?.address_longitude) {
      setIsLoadingLidarData(true);
      setIsLoadingOsmData(true);
      Promise.all([
        fetchLidarData(addressInfo.address_latitude, addressInfo.address_longitude, 10, addressInfo.cellId),
        fetchHeightData(addressInfo.address_latitude, addressInfo.address_longitude, columnWidth),
        fetchOsmRiskData(addressInfo.address_latitude, addressInfo.address_longitude)
      ])
        .then(([lidar, height, osmData]) => {
          setLidarData(lidar);
          setHeightData(height); // Keep for reference, but AGL is calculated from LiDAR data
          setOsmRiskData(osmData);
          console.log('🏠 OSM Risk Data loaded:', osmData);
        })
        .catch(error => {
          logToDatadog('error_loading_geo_data', { error, eircode, cellId: addressInfo.cellId }, 'error');
          notify({
            variant: 'error',
            message: 'Failed to load geographical data'
          });
        })
        .finally(() => {
          setIsLoadingLidarData(false);
          setIsLoadingOsmData(false);
        });
    }
  }, [addressInfo, columnWidth]);

  // Fetch geographical data when marker position changes (excluding OSM data - keep that for eircode only)
  useEffect(() => {
    console.log('🎯 useEffect triggered for markerPosition:', {
      markerPosition,
      activeTab,
      manualCellId,
      addressInfoCellId: addressInfo?.cellId,
      columnWidth
    });
    
    if (markerPosition && markerPosition.lat && markerPosition.lng) {
      // Use cellId from addressInfo if in eircode mode, otherwise use manual cell ID, fallback to blanchCellId
      const cellId = activeTab === 'eircode' 
        ? (addressInfo?.cellId || blanchCellId)
        : (parseInt(manualCellId) || blanchCellId);
      
      console.log('✅ Fetching geo data for position:', markerPosition, 'with cellId:', cellId, 'mode:', activeTab);
      
      // Don't proceed if we don't have a valid cell ID in coordinate mode
      if (activeTab === 'coordinates' && !manualCellId) {
        console.log('❌ No manual cell ID provided for coordinate mode');
        return;
      }
      
      setIsLoadingLidarData(true);
      setIsLoadingRiskEvaluation(true);
      setRiskEvaluationError(null);
      
      // Main data promises
      Promise.all([
        fetchLidarData(markerPosition.lat, markerPosition.lng, 10, cellId),
        fetchHeightData(markerPosition.lat, markerPosition.lng, columnWidth),
        fetchRiskEvaluationData(markerPosition.lat, markerPosition.lng)
      ])
        .then(([lidar, height, riskEvaluation]) => {
          console.log('📍 Promise.all resolved with data:', { 
            lidarPoints: lidar.length, 
            height, 
            riskEvaluation,
            position: markerPosition,
            cellId,
            mode: activeTab
          });
          setLidarData(lidar);
          setHeightData(height);
          setRiskEvaluationData(riskEvaluation);
        })
        .catch(error => {
          console.error('❌ Error fetching geo data:', error);
          logToDatadog('error_loading_geo_data_marker', { error, markerPosition, cellId, activeTab }, 'error');
          setRiskEvaluationError(error?.message || 'Failed to evaluate risk');
          // Show error notification for coordinate mode since it's intentional
          if (activeTab === 'coordinates') {
            notify({
              variant: 'error',
              message: 'Failed to load geographical data for coordinates'
            });
          }
        })
        .finally(() => {
          setIsLoadingLidarData(false);
          setIsLoadingRiskEvaluation(false);
        });
    } else {
      console.log('❌ No valid marker position:', markerPosition);
    }
  }, [markerPosition, addressInfo, columnWidth, activeTab, manualCellId]);

  // Helper function to calculate distance between two points in meters
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI/180;
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lng2-lng1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  };

  // Separate effect to fetch coordinate drop locations
  useEffect(() => {
    if (activeTab === 'coordinates' && markerPosition && markerPosition.lat && markerPosition.lng) {
      // Check if we need to make a new API call
      // Only fetch if this is the first call or if marker moved more than 50 meters
      const shouldFetch = !lastCoordinateApiCall || 
        calculateDistance(
          lastCoordinateApiCall.lat, lastCoordinateApiCall.lng,
          markerPosition.lat, markerPosition.lng
        ) > 50;

      if (shouldFetch) {
        setIsLoadingCoordinateDropLocations(true);
        setCoordinateDropLocations([]); // Clear previous data
        
        fetchDropLocationsByCoordinates(markerPosition.lat, markerPosition.lng)
          .then((dropLocations) => {
            setCoordinateDropLocations(dropLocations);
            setLastCoordinateApiCall({ lat: markerPosition.lat, lng: markerPosition.lng });
          })
          .catch((error) => {
            logToDatadog('error_fetching_coordinate_drop_locations', { error, markerPosition }, 'error');
            // Don't show error notification as this is supplementary data
          })
          .finally(() => {
            setIsLoadingCoordinateDropLocations(false);
          });
      }
    } else if (activeTab === 'eircode') {
      // Clear coordinate drop locations when switching to eircode mode
      setCoordinateDropLocations([]);
      setLastCoordinateApiCall(null);
    }
  }, [markerPosition, activeTab]);

  useEffect(() => {
    setIsFormValid(checkIsFormValid());
  }, [eircode]);

  // Load basic data on mount (no cell ID required)
  useEffect(() => {
    getCells();
    getGeofences();
  }, []);

  // Load obstacles only when we have an address with a cell ID
  useEffect(() => {
    if (addressInfo?.cellId) {
      getObstacles(addressInfo.cellId);
    }
  }, [addressInfo?.cellId]);

  const getRemarksForAddress = async (addressFindId: string) => {
    orderfulApi.getRemarksForAddressfind(addressFindId)
      .then((remarks) => {
        setAddressRemarks(remarks as unknown as AddressFindRemark[]);
      });
  };

  const saveRemarkForAddress = async (addressFindId: string, message: string) => {
    orderfulApi.createAddressFindRemark(addressFindId, {
      message,
      isVisibleToCustomer: true,
      addressFindId
    });
  };

  const checkIsFormValid = (): boolean => {
    return true; // Simplified validation
  };

  const fetchAddressInfo = async (eircode: string) => {
    if (!eircode) return;
    return API.get('api', `/addressfind/locationId/${eircode}`, {});
  };

  const onChangeEircode = (newEircode: string) => {
    const normalisedEircode = newEircode.replace(/\s/g, '').toUpperCase();
    const hasChanged = normalisedEircode !== eircode;
    
    setEircode(normalisedEircode);
    
    // Clear previous address data when eircode changes
    if (hasChanged && addressInfo) {
      setAddressInfo(null);
      setLidarData([]);
      setHeightData(null);
      setMarkerPosition(null);
      setAddressRemarks([]);
      setIsAddressEnabled(false);
      setAddressRemarkText('');
      setSelectedDropLocationId(null);
      setOsmRiskData(null);
      setRiskEvaluationData(null);
      setRiskEvaluationError(null);
      setManualCoords('');
      setCoordinateDropLocations([]); // Clear coordinate drop locations
      setLastCoordinateApiCall(null);
    }
  };

  const enableAddress = async (val: boolean) => {
    try {
      setIsEnablingAddress(true);
      updateAddressEnabledStatus(addressInfo.id, val);
      setIsAddressEnabled(val);
    } catch (error) {
      logToDatadog('error_enabling_address', { error, eircode, val }, 'error');
      notify({
        variant: 'error',
        message: error.errorMessage
      });
    }
    setIsEnablingAddress(false);
  };

  const checkIsWithinGeofence = (): boolean => {
    if (!cells) {
      return;
    }

    const selectedCell = cells?.find(cell => cell.id === blanchCellId);
    const addressPoint = point([addressInfo.address_longitude, addressInfo.address_latitude]);
    const cellGeofencePolygon = polygon(selectedCell?.geofence?.geoJson?.features[0]?.geometry?.coordinates);
    return booleanPointInPolygon(addressPoint, cellGeofencePolygon);
  };

  const loadAddressInfo = async () => {
    if (!eircode || !isFormValid || isLoadingAddressInfo) {
      return;
    }
    
    try {
      setIsLoadingAddressInfo(true);
      
      // Clear previous data
      setAddressInfo(null);
      setLidarData([]);
      setHeightData(null);
      setMarkerPosition(null);
      setAddressRemarks([]);
      setAddressRemarkText('');
      setSelectedDropLocationId(null);
      setOsmRiskData(null);
      setRiskEvaluationData(null);
      setRiskEvaluationError(null);
      setManualCoords('');
      setLastCoordinateApiCall(null); // Clear coordinate API cache
      
      const address = await fetchAddressInfo(eircode);
      console.log('🔍 Debug: Address loaded with cellId:', address.cellId, 'Type:', typeof address.cellId, 'Full address object:', address);
      setAddressInfo(address);
      setIsAddressEnabled(address.enabled);
      await getRemarksForAddress(address.id);
    } catch (error) {
      logToDatadog('error_fetching_address', { error, eircode }, 'error');
      notify({
        variant: 'error',
        message: error?.errorMessage || 'Failed to load address information'
      });
    } finally {
      setIsLoadingAddressInfo(false);
    }
  };

  const getEircodeCoords = (): { lat: number; lng: number } => {
    return {
      lat: addressInfo?.address_latitude || 53.39292547693334,
      lng: addressInfo?.address_longitude || -6.395731067422041
    };
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const onMarkerMove = useCallback((position: { lat: number; lng: number }) => {
    console.log('🔄 Marker moved to:', position);
    setMarkerPosition(position);
  }, []);

  // Function to handle manual coordinate input
  const handleManualCoordinates = () => {
    // Validate cell ID is provided
    if (!manualCellId.trim()) {
      notify({
        variant: 'error',
        message: 'Please enter a Cell ID'
      });
      return;
    }
    
    const cellId = parseInt(manualCellId.trim());
    if (isNaN(cellId)) {
      notify({
        variant: 'error',
        message: 'Please enter a valid Cell ID number'
      });
      return;
    }
    
    // Parse comma-separated coordinates
    const coords = manualCoords.trim().split(',');
    if (coords.length !== 2) {
      notify({
        variant: 'error',
        message: 'Please enter coordinates in format: latitude, longitude'
      });
      return;
    }
    
    const lat = parseFloat(coords[0].trim());
    const lng = parseFloat(coords[1].trim());
    
    // Validate coordinates
    if (isNaN(lat) || isNaN(lng)) {
      notify({
        variant: 'error',
        message: 'Please enter valid latitude and longitude values'
      });
      return;
    }
    
    if (lat < -90 || lat > 90) {
      notify({
        variant: 'error',
        message: 'Latitude must be between -90 and 90'
      });
      return;
    }
    
    if (lng < -180 || lng > 180) {
      notify({
        variant: 'error',
        message: 'Longitude must be between -180 and 180'
      });
      return;
    }
    
    // Clear any previous data first
    setLidarData([]);
    setHeightData(null);
    setRiskEvaluationData(null);
    setRiskEvaluationError(null);
    setSelectedDropLocationId(null);
    setLastCoordinateApiCall(null); // Force fresh drop location fetch
    
    // Set up delivery radius for the cell
    const newRadius = getDeliveryRadiusForCell(cellId);
    setColumnWidth(newRadius);
    
    // Set marker position which will trigger data fetching
    const newPosition = { lat, lng };
    setMarkerPosition(newPosition);
    
    // Center and zoom the map to the new position (if map is loaded)
    if (mapRef.current) {
      mapRef.current.setCenter(newPosition);
      mapRef.current.setZoom(21);
    }
    
    notify({
      variant: 'success',
      message: `Map updated to coordinates: ${lat.toFixed(6)}, ${lng.toFixed(6)} (Cell ${cellId})`
    });
  };

  // Function to copy coordinates to clipboard
  const copyCoordinatesToClipboard = () => {
    if (markerPosition) {
      const coordsText = `${markerPosition.lat.toFixed(6)}, ${markerPosition.lng.toFixed(6)}`;
      navigator.clipboard.writeText(coordsText).then(() => {
        notify({
          variant: 'success',
          message: 'Coordinates copied to clipboard'
        });
      }).catch(() => {
        notify({
          variant: 'error',
          message: 'Failed to copy coordinates'
        });
      });
    }
  };

  // Initialize marker position when address info is loaded
  useEffect(() => {
    if (addressInfo && !markerPosition) {
      setMarkerPosition(getEircodeCoords());
    }
  }, [addressInfo, markerPosition]);

  // Update delivery radius when address info changes or when manual cell ID changes
  useEffect(() => {
    const cellId = activeTab === 'eircode' 
      ? addressInfo?.cellId
      : parseInt(manualCellId) || null;
    
    if (cellId) {
      const newRadius = getDeliveryRadiusForCell(cellId);
      setColumnWidth(newRadius);
    }
  }, [addressInfo, activeTab, manualCellId]);



  // Update manual coordinate inputs when marker position changes
  useEffect(() => {
    if (markerPosition) {
      setManualCoords(`${markerPosition.lat}, ${markerPosition.lng}`);
    }
  }, [markerPosition]);

  // Auto-scroll to selected drop location in horizontal container
  useEffect(() => {
    const dropLocationHistory = activeTab === 'eircode' 
      ? addressInfo?.dropLocationHistory 
      : coordinateDropLocations;
      
    if (selectedDropLocationId && dropLocationHistory) {
      // Find the selected drop location in the history
      const selectedIndex = dropLocationHistory
        .filter(shouldShowDropLocation)
        .sort((a, b) => b.createDate - a.createDate)
        .findIndex(dropLocation => dropLocation.id === selectedDropLocationId);
      
      if (selectedIndex !== -1) {
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
          const container = document.querySelector('.drop-location-scroll-container');
          const cards = container?.querySelectorAll('.drop-location-card');
          const selectedCard = cards?.[selectedIndex];
          
          if (container && selectedCard) {
            selectedCard.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }
        }, 100);
      }
    }
  }, [selectedDropLocationId, addressInfo?.dropLocationHistory, coordinateDropLocations, activeTab]);



  const onDropLocationSelected = useCallback((dropLocationId: number, lat: number, lng: number) => {
    setSelectedDropLocationId(dropLocationId);
    setMarkerPosition({ lat, lng });
    
    // Zoom and center the map to the selected location
    if (mapRef.current) {
      mapRef.current.setCenter({ lat, lng });
      mapRef.current.setZoom(21); // Set to maximum zoom for detailed view
    }
    
    // Immediately start loading LiDAR data for the new position
    const cellId = activeTab === 'eircode' 
      ? (addressInfo?.cellId || blanchCellId)
      : (parseInt(manualCellId) || blanchCellId);
    
    if (cellId) {
      setIsLoadingLidarData(true);
      setIsLoadingRiskEvaluation(true);
      setRiskEvaluationError(null);
      
      Promise.all([
        fetchLidarData(lat, lng, 10, cellId),
        fetchHeightData(lat, lng, columnWidth),
        fetchRiskEvaluationData(lat, lng)
      ])
        .then(([lidar, height, riskEvaluation]) => {
          console.log('📍 Drop location Promise.all resolved with risk evaluation:', riskEvaluation, typeof riskEvaluation);
          setLidarData(lidar);
          setHeightData(height); // Keep for reference, but AGL is calculated from LiDAR data
          setRiskEvaluationData(riskEvaluation);
        })
        .catch(error => {
          logToDatadog('error_loading_geo_data_selected', { error, dropLocationId, lat, lng, cellId }, 'error');
          setRiskEvaluationError(error?.message || 'Failed to evaluate risk');
          notify({
            variant: 'error',
            message: 'Failed to load geographical data for selected location'
          });
        })
        .finally(() => {
          setIsLoadingLidarData(false);
          setIsLoadingRiskEvaluation(false);
        });
    }
  }, [mapRef, addressInfo?.cellId, fetchLidarData, fetchHeightData, notify, activeTab, manualCellId]);

  const formatDropLocationDate = (createDate: number): string => {
    return new Date(createDate).toLocaleDateString('en-IE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDropLocationDeliveryCount = (dropLocation: DropLocation): number => {
    return dropLocation?.okDeliveries || 0;
  };

  const getDropLocationRiskLevel = (dropLocation: DropLocation): 'HIGH' | 'LOW' | 'VERIFIED' | 'UNKNOWN' => {
    if (dropLocation?.risk) {
      return dropLocation.risk as 'HIGH' | 'LOW' | 'VERIFIED';
    }
    return 'UNKNOWN';
  };

  // Check if API key is available
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const hasGoogleMapsApiKey = googleMapsApiKey && googleMapsApiKey.length > 10;

  return (
    <div className='bg-white h-full'>
      <header className="p-6">
        <div className="md:flex md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-semibold leading-6 text-gray-900">Drop Point Suggestions v2</h2>
            <p className="mt-2 text-sm text-gray-700">
              For Blanchardstown cell only - Enhanced with Google Maps and 3D-style view.
            </p>
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>📍 Real-time LiDAR Analysis:</strong> Drag the blue marker, click anywhere on the map, or select from historical drop locations to analyze real LiDAR point cloud data and ground height information from our geo API.
              </p>
              <p className="text-xs text-blue-800 mt-1">
                <strong>🗂️ Drop Location History:</strong> Click on any historical drop location card or double-click map markers to automatically zoom to that location and load terrain analysis.
              </p>
              <p className="text-xs text-blue-800 mt-1">
                <strong>📏 Height Data:</strong> All heights are converted to AGL (Above Ground Level) for accurate drone obstacle assessment. Ground elevation is automatically calculated and subtracted from AMSL values.
              </p>
            </div>
          </div>
        </div>
      </header>
      
      <div className="p-6 space-y-8">
        {/* Tabbed Input Section */}
        <div className="w-full max-w-md">
          {/* Tab Headers */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('eircode')}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'eircode'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Eircode Lookup
              </button>
              <button
                onClick={() => setActiveTab('coordinates')}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'coordinates'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Coordinates
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="mt-4">
            {activeTab === 'eircode' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="eircode" className="block text-sm font-medium leading-6 text-gray-900">
                    Eircode
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="eircode"
                      id="eircode"
                      value={eircode}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onChangeEircode(e.target.value);
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          loadAddressInfo();
                        }
                      }}
                      autoComplete="postal-code"
                      className="pl-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                
                {eircode && !isFormValid ? (
                  <div className="p-2 text-sm text-red-700">
                    Invalid eircode
                  </div>
                ) : null}
                
                <button
                  type="button"
                  disabled={!isFormValid || isLoadingAddressInfo}
                  onClick={loadAddressInfo}
                  className={`w-full inline-flex items-center justify-center rounded-md ${
                    isFormValid 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-blue-200 cursor-not-allowed hover:bg-blue-200 text-slate-500'
                  } px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600`}
                >
                  Load address info {isLoadingAddressInfo ? <div className='ml-2'><LoadingSpinner size={20} /></div> : null}
                </button>
              </div>
            )}

            {activeTab === 'coordinates' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="manual-coords" className="block text-sm font-medium leading-6 text-gray-900">
                    Coordinates (latitude, longitude)
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="manual-coords"
                      id="manual-coords"
                      placeholder="53.397, -6.396"
                      value={manualCoords}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setManualCoords(e.target.value);
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          handleManualCoordinates();
                        }
                      }}
                      className="pl-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Enter coordinates separated by comma</p>
                </div>
                
                <div>
                  <label htmlFor="manual-cell-id" className="block text-sm font-medium leading-6 text-gray-900">
                    Cell ID
                  </label>
                  <div className="mt-2">
                    <input
                      type="number"
                      name="manual-cell-id"
                      id="manual-cell-id"
                      placeholder="7"
                      value={manualCellId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setManualCellId(e.target.value);
                      }}
                      className="pl-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Required for API calls</p>
                </div>
                
                <button
                  type="button"
                  onClick={handleManualCoordinates}
                  disabled={!manualCoords.trim() || !manualCellId.trim()}
                  className={`w-full inline-flex items-center justify-center rounded-md ${
                    manualCoords.trim() && manualCellId.trim()
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-green-200 cursor-not-allowed hover:bg-green-200 text-slate-500'
                  } px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600`}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Go to Coordinates
                </button>
                
                {markerPosition && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-blue-800">
                        <strong>Current marker:</strong> {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
                      </p>
                      <button
                        onClick={copyCoordinatesToClipboard}
                        className="text-xs text-blue-600 hover:text-blue-700 underline ml-2 flex items-center"
                        title="Copy coordinates to clipboard"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>



        {/* Drop Location History Section */}
        {((activeTab === 'eircode' && addressInfo?.dropLocationHistory && addressInfo.dropLocationHistory.length > 0) || 
          (activeTab === 'coordinates' && coordinateDropLocations.length > 0)) ? (
          <div className="w-full bg-white border border-gray-200 rounded-lg p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Drop Location History 
                {activeTab === 'coordinates' && (
                  <span className="text-sm font-normal text-blue-600 ml-2">
                    (for this coordinate area)
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Click on any previous drop location to automatically zoom the map, move the marker, and analyze LiDAR terrain data for that position
              </p>
              <p className="text-xs text-gray-500 mt-1">
                ← Scroll horizontally to see all locations →
              </p>
              {isLoadingCoordinateDropLocations && activeTab === 'coordinates' && (
                <div className="mt-2 text-xs text-blue-600 flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading drop locations for coordinates...
                </div>
              )}
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 px-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 drop-location-scroll-container" 
                 style={{ scrollbarWidth: 'thin', scrollSnapType: 'x mandatory' }}>
              {(activeTab === 'eircode' ? addressInfo.dropLocationHistory : coordinateDropLocations)
                .filter(shouldShowDropLocation)
                .sort((a, b) => b.createDate - a.createDate) // Show newest first
                .map((dropLocation: DropLocation) => {
                  const isSelected = selectedDropLocationId === dropLocation.id;
                  const deliveryCount = getDropLocationDeliveryCount(dropLocation);
                  const riskLevel = getDropLocationRiskLevel(dropLocation);
                  const isNewLocation = dropLocation.createDate > Date.now() - (7 * 24 * 60 * 60 * 1000); // Within last 7 days
                  
                  // A location is verified only if it has explicit VERIFIED risk status
                  const isVerified = riskLevel === 'VERIFIED';
                  
                  return (
                    <button
                      key={dropLocation.id}
                      onClick={() => onDropLocationSelected(dropLocation.id, dropLocation.latitude, dropLocation.longitude)}
                      className={`p-3 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md relative flex-shrink-0 w-72 scroll-snap-start drop-location-card ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300'
                      } ${isLoadingLidarData && isSelected ? 'opacity-75' : ''}`}
                      style={{ scrollSnapAlign: 'start' }}
                    >
                      {isLoadingLidarData && isSelected && (
                        <div className="absolute inset-0 bg-blue-50 bg-opacity-75 flex items-center justify-center rounded-lg">
                          <div className="flex items-center space-x-2">
                            <LoadingSpinner size={20} />
                            <span className="text-sm text-blue-600 font-medium">Loading terrain data...</span>
                          </div>
                        </div>
                      )}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${isSelected ? 'bg-blue-500' : 'bg-gray-300'}`} />
                          {/* Badges */}
                          <div className="flex flex-wrap gap-1.5">
                            {isVerified && (
                              <span 
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                style={{
                                  backgroundColor: 'rgba(156, 39, 176, 0.08)',
                                  color: '#9C27B0'
                                }}
                              >
                                Verified
                              </span>
                            )}
                            {isNewLocation && !isVerified && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                New
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-medium ${
                            deliveryCount > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {deliveryCount} deliveries
                          </div>
                          {riskLevel !== 'UNKNOWN' && !isVerified && (
                            <div className={`text-xs ${
                              riskLevel === 'HIGH' ? 'text-red-500' : 'text-green-500'
                            }`}>
                              {riskLevel} risk
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-0.5">
                        <div className="text-sm text-gray-700">
                          <strong>Created:</strong> {formatDropLocationDate(dropLocation.createDate)}
                        </div>
                        <div className="text-xs text-gray-500">
                          <strong>Coordinates:</strong> {dropLocation.latitude.toFixed(6)}, {dropLocation.longitude.toFixed(6)}
                        </div>
                        {dropLocation.isBackGardenApproved && (
                          <div className="text-xs text-blue-600">
                            ✓ Back garden approved
                          </div>
                        )}
                        {dropLocation.userCreated && (
                          <div className="text-xs text-purple-600">
                            ✓ User created
                          </div>
                        )}
                      </div>
                      
                      {dropLocation.remarks && dropLocation.remarks.length > 0 && (
                        <div className="mt-1.5 pt-1.5 border-t border-gray-100">
                          <div className="text-xs text-gray-500">
                            <strong>Remarks:</strong> {dropLocation.remarks.length} note(s)
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-1.5 pt-1.5 border-t border-gray-100">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDropLocationSelected(dropLocation.id, dropLocation.latitude, dropLocation.longitude);
                          }}
                          className={`w-full inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded transition-all duration-200 ${
                            isSelected 
                              ? 'bg-blue-600 text-white shadow-sm' 
                              : 'bg-gray-50 text-gray-700 hover:bg-blue-50 hover:text-blue-700 border border-gray-200'
                          }`}
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isSelected ? "M5 13l4 4L19 7" : "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"} />
                          </svg>
                          {isSelected ? 'Analyzing...' : 'Analyze'}
                        </button>
                      </div>
                    </button>
                  );
                })}
            </div>
            {(activeTab === 'eircode' ? addressInfo.dropLocationHistory : coordinateDropLocations).filter(shouldShowDropLocation).length === 0 && (
              <div className="flex items-center justify-center py-8 px-8 text-gray-500 min-w-full">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <p className="text-sm">
                    {isLoadingCoordinateDropLocations 
                      ? 'Loading drop locations...' 
                      : `No previous drop locations found for this ${activeTab === 'eircode' ? 'address' : 'coordinate area'}`
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Maps Section */}
        <div className="w-full">
          {(addressInfo || markerPosition) ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
              {/* Map Panel */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden w-full h-[680px] flex flex-col">
                <div className="p-3 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Map View</h3>
                      <p className="text-sm text-gray-600">Drag marker to select analysis location</p>
                      <p className="text-xs text-blue-600 mt-1">
                        Delivery area radius: {(() => {
                          const cellId = activeTab === 'eircode' 
                            ? addressInfo?.cellId
                            : parseInt(manualCellId) || null;
                          return cellId ? getDeliveryRadiusForCell(cellId) : 3;
                        })()}m
                      </p>

                      
                      {/* Risk Evaluation Display */}
                      <div className="mt-2 flex items-center space-x-2">
                        <div className="relative">
                          <span 
                            className="text-xs font-medium text-gray-700 cursor-help border-b border-dotted border-gray-400"
                            onMouseEnter={() => setShowBuildingRiskTooltip(true)}
                            onMouseLeave={() => setShowBuildingRiskTooltip(false)}
                          >
                            Building Risk:
                          </span>
                          {/* Tooltip */}
                          {showBuildingRiskTooltip && (
                            <div className="absolute left-0 top-full mt-2 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 w-96 z-50">
                              <div className="mb-2 font-semibold">Building Distance Requirements:</div>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-gray-600">
                                    <th className="text-left py-1 pr-2">If building is...</th>
                                    <th className="text-left py-1 pr-2">Max height...</th>
                                    <th className="text-left py-1">Think of it as...</th>
                                  </tr>
                                </thead>
                                <tbody className="text-xs">
                                  <tr>
                                    <td className="py-0.5 pr-2">Very close (0-3m)</td>
                                    <td className="py-0.5 pr-2">11m (3 stories)</td>
                                    <td className="py-0.5">Garden shed</td>
                                  </tr>
                                  <tr>
                                    <td className="py-0.5 pr-2">Close (3-8m)</td>
                                    <td className="py-0.5 pr-2">17m (5 stories)</td>
                                    <td className="py-0.5">Typical house</td>
                                  </tr>
                                  <tr>
                                    <td className="py-0.5 pr-2">Nearby (8-15m)</td>
                                    <td className="py-0.5 pr-2">21m (6 stories)</td>
                                    <td className="py-0.5">Small apartment</td>
                                  </tr>
                                  <tr>
                                    <td className="py-0.5 pr-2">Moderate (15-30m)</td>
                                    <td className="py-0.5 pr-2">34m (10 stories)</td>
                                    <td className="py-0.5">Office building</td>
                                  </tr>
                                  <tr>
                                    <td className="py-0.5 pr-2">Far (30-50m)</td>
                                    <td className="py-0.5 pr-2">45m (14 stories)</td>
                                    <td className="py-0.5">High-rise building</td>
                                  </tr>
                                </tbody>
                              </table>
                              {/* Arrow pointing up */}
                              <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-900"></div>
                            </div>
                          )}
                        </div>
                        {isLoadingRiskEvaluation ? (
                          <div className="flex items-center space-x-1">
                            <LoadingSpinner size={12} />
                            <span className="text-xs text-blue-600">Evaluating...</span>
                          </div>
                        ) : riskEvaluationError ? (
                          <span className="text-xs text-red-600">❌ {riskEvaluationError}</span>
                        ) : riskEvaluationData ? (
                          (() => {
                            console.log('🎯 Rendering risk evaluation data:', riskEvaluationData, typeof riskEvaluationData);
                            
                            // Building risk status
                            const buildingRiskStatus = riskEvaluationData === 'HIGH' ? '🔴 HIGH (Near building)' : 
                                                      riskEvaluationData === 'LOW' ? '🟢 LOW (Clear area)' : 
                                                      `Unknown: ${riskEvaluationData}`;
                            
                            // Ground risk status
                            let groundRiskStatus;
                            if (isLoadingLidarData) {
                              groundRiskStatus = (
                                <span className="inline-flex items-center space-x-1">
                                  <LoadingSpinner size={12} />
                                  <span className="text-blue-600">Loading...</span>
                                </span>
                              );
                            } else if (heightData === null || heightData === undefined || isNaN(heightData)) {
                              groundRiskStatus = '⚪ No height data';
                            } else if (heightData >= 1.0) {
                              groundRiskStatus = '🔴 Unsafe (≥ 1.0m obstacles detected)';
                            } else {
                              groundRiskStatus = '🟢 Safe (< 1.0m height)';
                            }
                            
                            return (
                              <div className="flex items-center space-x-4">
                                <span className={`text-xs font-medium ${
                                  riskEvaluationData === 'HIGH' 
                                    ? 'text-red-600' 
                                    : riskEvaluationData === 'LOW' 
                                      ? 'text-green-600' 
                                      : 'text-gray-600'
                                }`}>
                                  {buildingRiskStatus}
                                </span>
                                <span className="text-gray-400">•</span>
                                <span className="text-xs text-gray-500">
                                  Ground Risk: {groundRiskStatus}
                                </span>
                              </div>
                            );
                          })()
                        
                        ) : markerPosition ? (
                          <span className="text-xs text-gray-500">Move marker to evaluate</span>
                        ) : (
                          <span className="text-xs text-gray-400">No position selected</span>
                        )}
                      </div>
                    </div>
                    {markerPosition && (
                      <a
                        href={`https://manna-int.m4mga3.com/osrm/osrm-map.html#lat=${markerPosition.lat}&lng=${markerPosition.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Advanced Map Inspection Tool
                      </a>
                    )}
                  </div>
                  
                 </div>
                 <div className="flex-1 min-h-0">
                   {!hasGoogleMapsApiKey ? (
                     <div className="flex items-center justify-center h-full bg-gray-100">
                       <div className="text-center">
                         <h3 className="text-lg font-medium text-gray-900 mb-2">Google Maps API Key Required</h3>
                         <p className="text-sm text-gray-600 mb-4">
                           Please set the <code className="bg-gray-200 px-1 py-0.5 rounded">REACT_APP_GOOGLE_MAPS_API_KEY</code> environment variable.
                         </p>
                         <div className="text-xs text-gray-500">
                           <p>Current key status: {googleMapsApiKey ? `Set (${googleMapsApiKey.substring(0, 8)}...)` : 'Not set'}</p>
                         </div>
                       </div>
                     </div>
                   ) : mapLoadError ? (
                     <div className="flex items-center justify-center h-full bg-red-50">
                       <div className="text-center">
                         <h3 className="text-lg font-medium text-red-900 mb-2">Google Maps Load Error</h3>
                         <p className="text-sm text-red-600 mb-2">{mapLoadError}</p>
                         <button 
                           onClick={() => window.location.reload()} 
                           className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                         >
                           Retry
                         </button>
                       </div>
                     </div>
                   ) : (
                     <Wrapper 
                       apiKey={googleMapsApiKey}
                       render={(status) => {
                         switch (status) {
                           case 'LOADING':
                             return (
                               <div className="flex items-center justify-center h-full bg-blue-50">
                                 <div className="text-center">
                                   <LoadingSpinner size={40} />
                                   <p className="mt-2 text-sm text-blue-600">Loading Google Maps...</p>
                                 </div>
                               </div>
                             );
                           case 'FAILURE':
                             return (
                               <div className="flex items-center justify-center h-full bg-red-50">
                                 <div className="text-center">
                                   <h3 className="text-lg font-medium text-red-900 mb-2">Google Maps Failed to Load</h3>
                                   <p className="text-sm text-red-600">Please check your API key and network connection.</p>
                                 </div>
                               </div>
                             );
                           case 'SUCCESS':
                             return (
                                                             <GoogleMap
                                center={markerPosition || getEircodeCoords()}
                                zoom={21}
                                onLoad={onMapLoad}
                                onMarkerMove={onMarkerMove}
                                addressInfo={addressInfo}
                                obstacles={obstacles}
                                geofences={geofences}
                                lidarData={lidarData}
                                heightData={heightData}
                                columnWidth={columnWidth}
                                dropLocationHistory={activeTab === 'eircode' ? addressInfo?.dropLocationHistory : coordinateDropLocations}
                                selectedDropLocationId={selectedDropLocationId}
                                onDropLocationSelect={onDropLocationSelected}
                                shouldShowDropLocation={shouldShowDropLocation}
                                osmRiskData={osmRiskData}
                                isLoadingOsmData={isLoadingOsmData}
                                buildingRisk={riskEvaluationData}
                                isLoadingHeightData={isLoadingLidarData}
                                style={{ height: '100%', width: '100%' }}
                              />
                             );
                           default:
                             return (
                               <div className="flex items-center justify-center h-full bg-gray-100">
                                 <p className="text-gray-600">Initializing map...</p>
                               </div>
                             );
                         }
                       }}
                     />
                   )}
                 </div>
               </div>

              {/* LiDAR Analysis Panel */}
              <div className="w-full h-[680px] flex flex-col">
                {markerPosition ? (
                  <>
                    {/* Save Drop Location Section */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 flex-shrink-0">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">Save Drop Location</h4>
                          {heightData !== null ? (
                            <div className="flex items-center space-x-3">
                              <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                getRiskLevel(heightData) === 'HIGH' ? 'bg-red-100 text-red-800' :
                                getRiskLevel(heightData) === 'LOW' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {getRiskLevel(heightData) === 'HIGH' ? 'UNSAFE' : getRiskLevel(heightData) === 'LOW' ? 'SAFE' : 'UNKNOWN'}
                              </div>
                              <span className="text-xs text-gray-600">
                                Height: {heightData.toFixed(1)}m AGL
                              </span>
                              <span className="text-xs text-gray-500">
                                Coords: {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
                              </span>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">Loading height data...</p>
                          )}
                        </div>
                        
                        {/* Save Button */}
                        <div className="flex-shrink-0 ml-4">
                          {(() => {
                            // Get current cell ID
                            const currentCellId = activeTab === 'eircode' 
                              ? addressInfo?.cellId 
                              : parseInt(manualCellId) || blanchCellId;
                            
                            // Debug logging
                            console.log('🔍 Button Debug:', {
                              activeTab,
                              addressInfoCellId: addressInfo?.cellId,
                              manualCellId,
                              currentCellId,
                              currentCellIdType: typeof currentCellId,
                              heightData,
                              riskLevel: getRiskLevel(heightData)
                            });
                            
                            // Bypass safety restrictions for cell 5 (testing)
                            const isTestingCell = currentCellId === 5;
                            const isHighRisk = getRiskLevel(heightData) === 'HIGH';
                            
                            if (isHighRisk && !isTestingCell) {
                              return (
                                <button
                                  disabled
                                  className="inline-flex items-center rounded-md bg-red-200 px-3 py-2 text-sm font-semibold text-red-500 cursor-not-allowed"
                                  title="Cannot save high risk locations (height ≥ 1.0m)"
                                >
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  Cannot Save
                                </button>
                              );
                            } else {
                              return (
                                <button
                                  onClick={saveSuggestedDropLocation}
                                  disabled={isSavingDropLocation || (!isTestingCell && heightData === null) || !markerPosition || (activeTab === 'eircode' && !addressInfo)}
                                  className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm ${
                                    isHighRisk && isTestingCell 
                                      ? 'bg-orange-600 hover:bg-orange-700' 
                                      : isTestingCell 
                                        ? 'bg-orange-600 hover:bg-orange-700'
                                        : 'bg-green-600 hover:bg-green-700'
                                  } text-white ${isSavingDropLocation || (!isTestingCell && heightData === null) || !markerPosition || (activeTab === 'eircode' && !addressInfo) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  title={isTestingCell ? 'Testing mode: All restrictions bypassed for Cell 5' : ''}
                                >
                                  {isSavingDropLocation ? (
                                    <>
                                      <LoadingSpinner size={16} />
                                      <span className="ml-2">Saving...</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                      </svg>
                                                                             {isTestingCell ? 'Save (TEST MODE)' : 'Save Drop Location'}
                                    </>
                                  )}
                                </button>
                              );
                            }
                          })()}
                        </div>
                      </div>


                    </div>

                    {/* LiDAR Visualization */}
                    <div className="flex-1 min-h-0">
                      <LidarVisualization3D
                        position={markerPosition}
                        radius={10}
                        lidarData={lidarData}
                        onDataLoad={(data) => {}}
                        onColumnWidthChange={setColumnWidth}
                        columnWidth={columnWidth}
                      />
                    </div>
                  </>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg h-full flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <p className="text-sm">Move the marker on the map to start 3D LiDAR analysis</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Address Information Section */}
        {addressInfo ? (
          <div className="w-full bg-white border border-gray-200 rounded-lg p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-900 mb-1">Address</dt>
                  <dd className={`text-sm leading-6 ${
                    checkIsWithinGeofence() ? 'text-gray-700' : 'text-red-700'
                  }`}>
                    {addressInfo?.address || ''}
                    {addressInfo && checkIsWithinGeofence() ? (
                      <div className="mt-1 text-sm text-green-700 font-medium">
                        ✓ Inside geofence
                      </div>
                    ) : (
                      // Geofence warning hidden but logic preserved
                      <div className="mt-1 text-sm text-red-700 font-bold" style={{ display: 'none' }}>
                        ⚠ Outside geofence
                      </div>
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-900 mb-2">
                    {isAddressEnabled ? "Address Enabled" : "Address Disabled"} - WARNING Changing switch value will immediately enable/disable address
                  </dt>
                  <Field as="div" className="flex items-center">
                    <Switch
                      disabled={isEnablingAddress}
                      checked={isAddressEnabled}
                      onChange={(isChecked) => enableAddress(isChecked)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
                        isAddressEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          isAddressEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </Switch>
                    <Label as="span" className="ml-3 text-sm">
                      <span className="font-medium text-gray-900">{isAddressEnabled ? 'Disable' : 'Enable'} {eircode}</span>
                    </Label>
                  </Field>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="drop-point-remark" className="block text-sm font-medium text-gray-900 mb-1">
                    If address is unsuitable, select a reason <span className='font-bold'>(visible to customer)</span>
                  </label>
                  <div className="mt-2">
                    <SelectDropdown
                      value={addressRemarkText}
                      onChange={(v: string) => setAddressRemarkText(v)}
                      options={addressRemarkDropdownOptions}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (addressRemarkText) {
                        saveRemarkForAddress(addressInfo.id, addressRemarkText)
                          .then(() => setAddressRemarkText(''));
                      }
                    }}
                    className="mt-3 inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                  >
                    Save note
                  </button>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-900 mb-1">Past remarks</dt>
                  <dd className="text-sm leading-6 text-gray-700">
                    {addressRemarks?.length > 0 ? (
                      <ul className='space-y-1 max-h-20 overflow-y-auto'>
                        {addressRemarks?.map((remark: AddressFindRemark) => {
                          return (
                            <li key={remark.id} className="text-xs text-gray-600 px-2 py-1 bg-gray-50 rounded">
                              {addressRemarkDropdownOptions.find(opt => opt.value === remark.message)?.displayText || remark.message}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <span className="text-xs text-gray-500">None</span>
                    )}
                  </dd>
                </div>

                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShouldShowModal(true)}
                    className="w-full inline-flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                  >
                    Suggest drop points
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

      </div>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  cells: state.orders.cells,
  selectedCellId: state.orders.selectedCellId,
  geofences: state.geofences.geofences,
  obstacles: state.obstacles.list,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  notify: (notification: Notification) => dispatch(newNotification(notification)),
  updateAddressEnabledStatus: (addressId: string, isEnabled: boolean) => 
    dispatch(updateAddressEnabledStatus(addressId, isEnabled)),
  getCells: () => dispatch(getCells()),
  getGeofences: () => dispatch(getGeofences()),
  getObstacles: (cellId: number) => dispatch(getObstacles(cellId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DropPointSuggestorV2);